import { useMemo } from 'react';
import { useParams } from 'react-router';

export interface VisitScope {
  /** Empty string when there is no profile to scope to. */
  profileId: string;
  visitId?: string;
  /**
   * True when the records on screen belong to a single visit rather than to a
   * patient's profile — i.e. a guest visit, where no profile exists.
   */
  isVisitScoped: boolean;
}

/**
 * Which record set a visit tab should read: the patient's profile, or just this
 * visit.
 *
 * Profile if there is one, visit otherwise. The same rule the backend endpoints
 * follow when they branch on `visit.profileId`, and the same one the assistant
 * reports as `hasProfile`.
 *
 * The fallback is decided by whether a profile id is in the route, not by
 * whether the profile happens to hold any records: anything added during an
 * authed visit is written *with* `profileId` set, so a profile query already
 * returns it and a content-based fallback could never fire.
 */
export function useVisitScope(): VisitScope {
  const { profileId, patientId, visitId } = useParams<{
    profileId?: string;
    patientId?: string;
    visitId?: string;
  }>();

  return useMemo(() => {
    const resolvedProfileId = profileId ?? patientId ?? '';
    return {
      profileId: resolvedProfileId,
      visitId,
      isVisitScoped: !resolvedProfileId && Boolean(visitId),
    };
  }, [profileId, patientId, visitId]);
}
